import SuggestInput from "@/components/common/SuggestInput";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, EyeOff, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatMoney, formatNumber, formatPct, formatTRY, imgSrc } from "@/lib/format";
import { EmptyRow, LoadingRow, PageHeader, Pager } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import ProductDialog from "@/components/products/ProductDialog";
import ImportWizard from "@/components/import/ImportWizard";
import PriceUpdateDialog from "@/components/import/PriceUpdateDialog";
import BulkCategory from "@/components/products/BulkCategory";
import LabelDialog from "@/components/labels/LabelDialog";
import CodeGenDialog from "@/components/products/CodeGenDialog";
import { toast } from "sonner";
import { apiError } from "@/lib/api";

const PAGE_SIZE = 25;

function ProductRow({ p, showCost, showProfit, onOpen, onEdit, sel, onSel }) {
  const low = p.is_critical;
  return (
    <tr data-testid={`product-row-${p.sku}`} onClick={() => onOpen(p.id)} className="cursor-pointer hover:bg-blue-50/60">
      {onSel && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" className="h-4 w-4" checked={sel} onChange={() => onSel(p.id)} data-testid={`product-select-${p.sku}`} /></td>}
      <td><img src={imgSrc(p.image_url)} alt="" className="h-9 w-9 rounded object-cover border border-slate-200 bg-slate-50" /></td>
      <td className="font-mono text-xs font-medium text-slate-900">{p.sku}</td>
      <td className="font-mono text-xs text-slate-500">{p.barcode}</td>
      <td className="max-w-[240px] truncate font-medium text-slate-800" title={p.name}>{p.name}</td>
      <td>{p.brand}</td>
      <td className="text-slate-500">{p.category}</td>
      <td className="text-right font-mono">{p.koli_ici}</td>
      {showCost && <td className="text-right font-mono" data-testid={`product-purchase-${p.sku}`}>{formatMoney(p.purchase_price, p.purchase_currency)}</td>}
      {showCost && <td className="text-right font-mono" data-testid={`product-cost-${p.sku}`}>{formatTRY(p.real_cost)}</td>}
      <td className="text-right font-mono font-semibold text-slate-900">{formatTRY(p.sale_price)}</td>
      {showProfit && <td className="text-right font-mono text-emerald-700" data-testid={`product-margin-${p.sku}`}>{formatPct(p.margin_pct)}</td>}
      <td className={`text-right font-mono ${low ? "text-rose-600 font-semibold" : ""}`} data-testid={`product-available-${p.sku}`}>{formatNumber(p.available_qty)}</td>
      <td className="text-right font-mono text-slate-500">{formatNumber(p.min_stock)}</td>
      <td className="font-mono text-xs">{p.raf}</td>
      <td>{p.warehouse_name}</td>
      <td className="max-w-[180px] truncate" title={p.supplier_name}>{p.supplier_name}</td>
      <td>{p.origin}</td>
      {onEdit && <td onClick={(e) => e.stopPropagation()}><button type="button" title="Düzenle" onClick={() => onEdit(p.id)} className="p-1.5 rounded text-slate-500 hover:text-blue-700 hover:bg-blue-50" data-testid={`product-edit-${p.sku}`}><Pencil className="h-4 w-4" /></button></td>}
    </tr>
  );
}

export default function Products() {
  const { flag, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const openId = params.get("id");
  const navigate = useNavigate();
  const setOpen = (id) => setParams(id ? { id } : {});
  const [reload, setReload] = useState(0);
  const [bulk, setBulk] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [res, setRes] = useState(null);

  useEffect(() => { api.get("/products/categories").then(({ data }) => setCategories(data)); }, []);
  useEffect(() => {
    setRes(null);
    const t = setTimeout(() => {
      api.get("/products", { params: { q, category: category === "all" ? "" : category, page, page_size: PAGE_SIZE } }).then(({ data }) => setRes(data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category, page, reload]);

  const showCost = flag("cost_view");
  const showProfit = flag("profit_view");
  const canBulk = can("urunler", "edit");
  const [picked, setPicked] = useState([]);
  const [labels, setLabels] = useState(null);
  const pickedRows = () => (res?.items || []).filter((p) => picked.includes(p.id));
  const [codeGen, setCodeGen] = useState(false);
  const genBarcodes = async (body, label) => {
    if (!window.confirm(`${label} için GS1 GTIN-13 barkod üretilsin mi (merkezi kayıt defteri)? Mevcut barkodlara dokunulmaz.`)) return;
    try { const { data } = await api.post("/barcodes/generate", body); toast.success(`${data.count} ürüne barkod üretildi${data.skipped_existing ? ` · ${data.skipped_existing} ürünün barkodu zaten vardı` : ""}`); setReload((x) => x + 1); } catch (e) { toast.error(apiError(e)); }
  };
  const cols = 13 + (showCost ? 2 : 0) + (showProfit ? 1 : 0) + (canBulk ? 1 : 0);

  return (
    <div data-testid="products-page">
      <PageHeader title="Ürünler" subtitle="Ürün kartları, fiyatlar ve depo konumları" testid="products-title">
        {!showCost && <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-slate-200 bg-white text-slate-500" data-testid="cost-hidden-badge"><EyeOff className="h-3.5 w-3.5" />Maliyet alanları gizli</span>}
        {can("urunler", "create") && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setOpen("new")} data-testid="product-create-button"><Plus className="h-4 w-4 mr-1" />Yeni Ürün</Button>}
        {can("urunler", "edit") && <>
          <Button size="sm" variant="outline" onClick={() => setBulk("urunler")} data-testid="product-bulk-import-button">Excel ile Toplu Ürün Yükle/Güncelle</Button>
          <Button size="sm" variant="outline" onClick={() => setBulk("fiyat")} data-testid="product-bulk-price-button">Toplu Fiyat Güncelle</Button>
          <Button size="sm" variant="outline" onClick={() => genBarcodes({ all_missing: !q && category === "all", q, category: category === "all" ? "" : category }, q || category !== "all" ? "Filtredeki barkodsuz ürünler" : "Tüm barkodsuz ürünler")} data-testid="product-barcode-generate-missing">Barkod Üret (eksikler)</Button></>}
        {can("urunler", "create") && <Button size="sm" variant="outline" onClick={() => setCodeGen(true)} data-testid="product-codegen-button">Kod Üret</Button>}
      </PageHeader>
      {codeGen && <CodeGenDialog onClose={() => setCodeGen(false)} />}
      {bulk === "urunler" && <ImportWizard importer="urunler" title="Excel ile Toplu Ürün Yükle/Güncelle" onClose={() => setBulk(null)} onDone={() => setReload((r) => r + 1)} />}
      {bulk === "fiyat" && <PriceUpdateDialog onClose={() => setBulk(null)} onDone={() => setReload((r) => r + 1)} />}
      {openId && <ProductDialog productId={openId} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); setReload((r) => r + 1); }} />}
      <div className="panel overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-2 p-3 border-b border-slate-200">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <SuggestInput className="[&_input]:pl-8" scope="products" placeholder="Ürün adı, SKU, barkod, marka..." value={q} onChange={(v) => { setQ(v); setPage(1); }} testid="product-search-input" />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="h-9 sm:w-56" data-testid="product-category-select"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm kategoriler</SelectItem>
              {categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto max-h-[calc(100vh-260px)]">
          <table className="erp-table" data-testid="products-table">
            <thead>
              <tr>
                {canBulk && <th><input type="checkbox" className="h-4 w-4" checked={!!res?.items.length && res.items.every((p) => picked.includes(p.id))}
                  onChange={(e) => setPicked(e.target.checked ? [...new Set([...picked, ...(res?.items || []).map((p) => p.id)])] : picked.filter((x) => !(res?.items || []).some((p) => p.id === x)))} data-testid="product-select-all" /></th>}
                <th></th><th>SKU</th><th>Barkod</th><th>Ürün</th><th>Marka</th><th>Kategori</th><th className="text-right">Koli İçi</th>
                {showCost && <th className="text-right" data-testid="col-purchase-price">Alış Fiyatı</th>}
                {showCost && <th className="text-right" data-testid="col-real-cost">Gerçek Maliyet</th>}
                <th className="text-right">Satış Fiyatı</th>
                {showProfit && <th className="text-right" data-testid="col-margin">Marj</th>}
                <th className="text-right">Kullanılabilir</th><th className="text-right">Min.</th><th>Raf</th><th>Depo</th><th>Tedarikçi</th><th>Menşei</th>
              </tr>
            </thead>
            <tbody>
              {!res ? <LoadingRow cols={cols} /> : res.items.length === 0 ? (res.total === 0 && !q && category === "all" && can("urunler", "edit") ? (
                <tr><td colSpan={cols}><div className="py-10 px-4 max-w-xl" data-testid="products-empty-state">
                  <div className="text-lg font-semibold text-slate-900">Henüz ürün yok</div>
                  <p className="text-sm text-slate-600 mt-1">BizimHesap ürün listenizi tek seferde aktarın:</p>
                  <ol className="text-[13px] text-slate-600 mt-2 list-decimal pl-5 space-y-0.5">
                    <li>BizimHesap → <b>Ürünler</b> → <b>Excel'e aktar</b> ile listeyi indirin.</li>
                    <li>Aşağıdaki düğmeyle dosyayı seçin; başlıklar otomatik eşlenir.</li>
                    <li>Önizlemede KDV dönüşümü, hizmet satırları, açılış stoğu ve hataları kontrol edip onaylayın. Aktarım geri alınabilir.</li>
                  </ol>
                  <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => setBulk("urunler")} data-testid="products-empty-import-button">BizimHesap ürün listesini yükle</Button>
                </div></td></tr>) : <EmptyRow cols={cols} />) :
                res.items.map((p) => <ProductRow key={p.id} p={p} onOpen={(id) => navigate(`/urunler/${id}`)} onEdit={can("urunler", "edit") ? setOpen : null} sel={picked.includes(p.id)} onSel={canBulk ? (id) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id])) : null} showCost={showCost && "real_cost" in p} showProfit={showProfit && "margin_pct" in p} />)}
            </tbody>
          </table>
        </div>
        {picked.length > 0 && <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-blue-50/50 px-3 py-2" data-testid="product-selection-bar">
          <span className="text-[13px] font-medium">{picked.length} ürün seçili</span>
          <Button size="sm" variant="outline" onClick={() => setLabels(pickedRows().map((p) => ({ product_id: p.id, sku: p.sku, name: p.name, copies: 1 })))} data-testid="product-labels-button">Etiket Bas</Button>
          <Button size="sm" variant="outline" onClick={() => genBarcodes({ product_ids: picked }, `${picked.length} seçili ürün`)} data-testid="product-barcode-generate-selected">Barkod Üret</Button></div>}
        {labels && <LabelDialog items={labels} onClose={() => setLabels(null)} />}
        {picked.length > 0 && <BulkCategory ids={picked} onDone={() => { setPicked([]); setReload((x) => x + 1); }} />}
        {res && <Pager page={page} pageSize={PAGE_SIZE} total={res.total} onPage={setPage} testid="products-pager" />}
      </div>
    </div>
  );
}
